import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

import { AppShell } from "./app-shell";
import { RequireAuth } from "./features/authentication";
import { PermissionEnum } from "./features/permissions";
import { ErrorFallback } from "./features/ui/error-boundary";
import {
  ChartOfAccountPreferences,
  ChartOfAccounts,
  Companies,
  CostOfGoodsSoldReport,
  CreateCompany,
  CreateDeliveryCustomerClaim,
  CreateDeliveryOrder,
  CreateExpenseAccrual,
  CreateGoodsReceipt,
  CreateInvertedLabPurchaseOrder,
  CreateInvertedStandardPurchaseOrder,
  CreateJournalVoucher,
  CreateLensProduct,
  CreateManualSalesOrder,
  CreateOutletChartOfAccount,
  CreateOtherPayment,
  CreateProduct,
  CreatePurchaseDownPayment,
  CreatePurchaseInvoice,
  CreatePurchaseInvoiceFromSalesInvoice,
  CreatePurchaseOrder,
  CreatePurchaseOrderLab,
  CreatePurchasePayment,
  CreatePurchaseReturn,
  CreateReceiveCustomerClaim,
  CreateRole,
  CreateSalesDownPayment,
  CreateSalesInvoice,
  CreateSalesOrder,
  CreateSalesPayment,
  CreateSalesPaymentFromPurchasePayment,
  CreateSalesReturn,
  CreateSalesReturnFromPurchaseReturn,
  CreateStockAdjustment,
  CreateSupplier,
  CreateUser,
  CreateWarehouse,
  CustomerClaim,
  Customers,
  Dashboard,
  DefaultAccounts,
  DeliveryCustomerClaims,
  DeliveryOrder,
  DeliveryOrders,
  EditCompany,
  EditExpenseAccrual,
  EditDeliveryOrder,
  EditGoodsReceipt,
  EditJournalVoucher,
  EditLensProduct,
  EditOutletChartOfAccount,
  EditProduct,
  EditPurchaseDownPayment,
  EditPurchaseInvoice,
  EditPurchaseOrder,
  EditPurchaseOrderLab,
  EditPurchasePayment,
  EditRole,
  EditSalesDownPayment,
  EditSalesInvoice,
  EditSalesOrder,
  EditSalesPayment,
  EditOtherPayment,
  EditStockAdjustment,
  EditSupplier,
  EditUser,
  EditWarehouse,
  ExpenseCategories,
  ExpenseAccrual,
  ExpenseAccruals,
  ExpenseReport,
  Expenses,
  GoodsReceipt,
  GoodsReceipts,
  IncomingPurchasePayments,
  IncomingPurchaseReturns,
  IncomingSalesInvoices,
  InvertedLabPurchaseOrders,
  InventoryValueReport,
  JournalVoucher,
  JournalVouchers,
  LensProducts,
  Login,
  NotFound,
  OutletChartOfAccount,
  OutletChartOfAccounts,
  OutletPaymentMethod,
  Outlets,
  OtherPayment,
  OtherPayments,
  PaymentMethods,
  PaymentTerms,
  ProductCategories,
  Products,
  ProductVariantAttributes,
  CreatePromotion,
  EditPromotion,
  Promotion,
  PromotionCampaigns,
  Promotions,
  PurchaseDownPayment,
  PurchaseDownPayments,
  PurchaseInvoice,
  PurchaseInvoices,
  PurchaseOrder,
  PurchaseOrderLab,
  PurchaseOrders,
  PurchaseOrdersLab,
  PurchasePayment,
  PurchasePayments,
  PurchaseReturn,
  PurchaseReturns,
  ReceiveCustomerClaims,
  RenewPassword,
  Roles,
  SalesDownPayment,
  SalesDownPayments,
  SalesInvoice,
  SalesInvoices,
  SalesOrder,
  SalesOrderDefaultGeneralProductVariants,
  SalesOrderPaymentReport,
  SalesOrderProductVariantHubReport,
  SalesOrderProductVariantRetailReport,
  SalesOrderReport,
  SalesOrders,
  SalesPayment,
  SalesPayments,
  SalesRequest,
  SalesRequests,
  SalesReturn,
  SalesReturns,
  StockAdjustment,
  StockAdjustments,
  SupplierCategories,
  Suppliers,
  TrialBalance,
  UnitOfMeasurements,
  User,
  UserOutlet,
  Users,
  Warehouses,
  WarehouseStocks,
} from "./pages";

const router = createBrowserRouter([
  {
    index: true,
    element: <Login />,
    errorElement: <ErrorFallback />,
  },
  {
    errorElement: <ErrorFallback />,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        path: "dashboard",
        element: <Dashboard />,
      },
      {
        path: "setting",
        children: [
          {
            path: "users",
            children: [
              {
                index: true,
                element: <Users />,
                handle: { permission: PermissionEnum.READ_USERS },
              },
              {
                path: "create",
                element: <CreateUser />,
                handle: { permission: PermissionEnum.CREATE_USERS },
              },
              {
                path: ":userId",
                children: [
                  {
                    index: true,
                    element: <User />,
                    handle: { permission: PermissionEnum.READ_USERS },
                  },
                  {
                    path: "edit",
                    element: <EditUser />,
                    handle: { permission: PermissionEnum.UPDATE_USERS },
                  },
                  {
                    path: "outlets",
                    element: <UserOutlet />,
                    handle: { permission: PermissionEnum.READ_USER_OUTLET },
                  },
                ],
              },
            ],
          },
          {
            path: "roles",
            children: [
              {
                index: true,
                element: <Roles />,
                handle: { permission: PermissionEnum.READ_ROLES },
              },
              {
                path: "create",
                element: <CreateRole />,
                handle: { permission: PermissionEnum.CREATE_ROLES },
              },
              {
                path: ":roleId/edit",
                element: <EditRole />,
                handle: { permission: PermissionEnum.UPDATE_ROLES },
              },
            ],
          },
          {
            path: "promotion-campaigns",
            element: <PromotionCampaigns />,
            handle: { permission: PermissionEnum.READ_PROMOTIONS },
          },
          {
            path: "promotions",
            children: [
              {
                index: true,
                element: <Promotions />,
                handle: { permission: PermissionEnum.READ_PROMOTIONS },
              },
              {
                path: "create",
                element: <CreatePromotion />,
                handle: { permission: PermissionEnum.CREATE_PROMOTIONS },
              },
              {
                path: ":promotionId/edit",
                element: <EditPromotion />,
                handle: { permission: PermissionEnum.UPDATE_PROMOTIONS },
              },
              {
                path: ":promotionId",
                element: <Promotion />,
                handle: { permission: PermissionEnum.READ_PROMOTIONS },
              },
            ],
          },
          {
            path: "outlets",
            children: [
              {
                index: true,
                element: <Outlets />,
                handle: { permission: PermissionEnum.READ_OUTLETS },
              },
              {
                path: ":outletId",
                children: [
                  {
                    path: "payment-methods",
                    element: <OutletPaymentMethod />,
                    handle: {
                      permission: PermissionEnum.UPDATE_OUTLETS,
                    },
                  },
                ],
              },
            ],
          },
          {
            path: "companies",
            children: [
              {
                index: true,
                element: <Companies />,
                handle: { permission: PermissionEnum.READ_COMPANIES },
              },
              {
                path: "create",
                element: <CreateCompany />,
                handle: { permission: PermissionEnum.CREATE_COMPANIES },
              },
              {
                path: ":companyId/edit",
                element: <EditCompany />,
                handle: { permission: PermissionEnum.UPDATE_COMPANIES },
              },
            ],
          },
          {
            path: "accounting",
            children: [
              {
                index: true,
                element: <Navigate to="preferences" replace />,
              },
              {
                path: "preferences",
                element: <ChartOfAccountPreferences />,
                handle: {
                  permission:
                    PermissionEnum.READ_ACCOUNTING_ACTION_CHART_OF_ACCOUNT_PREFERENCES,
                },
              },
              {
                path: "default-accounts",
                element: <DefaultAccounts />,
                handle: {
                  permission:
                    PermissionEnum.READ_ACCOUNTING_ACTION_CHART_OF_ACCOUNT,
                },
              },
            ],
          },
        ],
      },
      {
        path: "expense",
        children: [
          {
            index: true,
            element: <Navigate to="expenses" replace />,
          },
          {
            path: "expenses",
            element: <Expenses />,
            handle: { permission: PermissionEnum.READ_EXPENSES },
          },
          {
            path: "expense-categories",
            element: <ExpenseCategories />,
            handle: { permission: PermissionEnum.READ_EXPENSE_CATEGORIES },
          },
          {
            path: "expense-accruals",
            children: [
              {
                index: true,
                element: <ExpenseAccruals />,
                handle: { permission: PermissionEnum.READ_EXPENSE_ACCRUALS },
              },
              {
                path: "create",
                element: <CreateExpenseAccrual />,
                handle: { permission: PermissionEnum.CREATE_EXPENSE_ACCRUALS },
              },
              {
                path: ":referenceNumber",
                element: <ExpenseAccrual />,
                handle: { permission: PermissionEnum.READ_EXPENSE_ACCRUALS },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditExpenseAccrual />,
                handle: { permission: PermissionEnum.UPDATE_EXPENSE_ACCRUALS },
              },
            ],
          },
        ],
      },
      {
        path: "procurement",
        children: [
          {
            path: "purchase-orders",
            children: [
              {
                path: "standard",
                children: [
                  {
                    index: true,
                    element: <PurchaseOrders />,
                    handle: {
                      permission: PermissionEnum.READ_PURCHASE_ORDERS_STANDARD,
                    },
                  },
                  {
                    path: "create",
                    element: <CreatePurchaseOrder />,
                    handle: {
                      permission:
                        PermissionEnum.CREATE_PURCHASE_ORDERS_STANDARD,
                    },
                  },
                  {
                    path: "create-inverted",
                    element: <CreateInvertedStandardPurchaseOrder />,
                    handle: {
                      permission:
                        PermissionEnum.CREATE_PURCHASE_ORDERS_STANDARD,
                    },
                  },
                  {
                    path: ":referenceNumber/edit",
                    element: <EditPurchaseOrder />,
                    handle: {
                      permission: PermissionEnum.READ_PURCHASE_ORDERS_STANDARD,
                    },
                  },
                  {
                    path: ":referenceNumber",
                    element: <PurchaseOrder />,
                    handle: {
                      permission: PermissionEnum.READ_PURCHASE_ORDERS_STANDARD,
                    },
                  },
                ],
              },
              {
                path: "lab",
                children: [
                  {
                    index: true,
                    element: <PurchaseOrdersLab />,
                    handle: {
                      permission: PermissionEnum.READ_PURCHASE_ORDERS_LAB,
                    },
                  },
                  {
                    path: "create",
                    element: <CreatePurchaseOrderLab />,
                    handle: {
                      permission: PermissionEnum.CREATE_PURCHASE_ORDERS_LAB,
                    },
                  },
                  {
                    path: ":referenceNumber/edit",
                    element: <EditPurchaseOrderLab />,
                    handle: {
                      permission: PermissionEnum.UPDATE_PURCHASE_ORDERS_LAB,
                    },
                  },
                  {
                    path: ":referenceNumber",
                    element: <PurchaseOrderLab />,
                    handle: {
                      permission: PermissionEnum.READ_PURCHASE_ORDERS_LAB,
                    },
                  },
                ],
              },
              {
                path: "lab-inverted",
                children: [
                  {
                    index: true,
                    element: <InvertedLabPurchaseOrders />,
                    handle: {
                      permission: PermissionEnum.READ_PURCHASE_ORDERS_LAB,
                    },
                  },
                  {
                    path: ":salesOrderLensProductId/create",
                    element: <CreateInvertedLabPurchaseOrder />,
                    handle: {
                      permission: PermissionEnum.CREATE_PURCHASE_ORDERS_LAB,
                    },
                  },
                ],
              },
            ],
          },
          {
            path: "goods-receipts",
            children: [
              {
                index: true,
                element: <GoodsReceipts />,
                handle: { permission: PermissionEnum.READ_GOODS_RECEIPTS },
              },
              {
                path: "create",
                element: <CreateGoodsReceipt />,
                handle: { permission: PermissionEnum.CREATE_GOODS_RECEIPTS },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditGoodsReceipt />,
                handle: { permission: PermissionEnum.READ_GOODS_RECEIPTS },
              },
              {
                path: ":referenceNumber",
                element: <GoodsReceipt />,
                handle: { permission: PermissionEnum.READ_GOODS_RECEIPTS },
              },
            ],
          },
          {
            path: "purchase-invoices",
            children: [
              {
                index: true,
                element: <PurchaseInvoices />,
                handle: { permission: PermissionEnum.READ_PURCHASE_INVOICES },
              },
              {
                path: "create",
                element: <CreatePurchaseInvoice />,
                handle: { permission: PermissionEnum.CREATE_PURCHASE_INVOICES },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditPurchaseInvoice />,
                handle: { permission: PermissionEnum.READ_PURCHASE_INVOICES },
              },
              {
                path: ":referenceNumber",
                element: <PurchaseInvoice />,
                handle: { permission: PermissionEnum.READ_PURCHASE_INVOICES },
              },
            ],
          },
          {
            path: "purchase-down-payments",
            children: [
              {
                index: true,
                element: <PurchaseDownPayments />,
                handle: {
                  permission: PermissionEnum.READ_PURCHASE_DOWN_PAYMENTS,
                },
              },
              {
                path: "create",
                element: <CreatePurchaseDownPayment />,
                handle: {
                  permission: PermissionEnum.CREATE_PURCHASE_DOWN_PAYMENTS,
                },
              },
              {
                path: ":referenceNumber",
                element: <PurchaseDownPayment />,
                handle: {
                  permission: PermissionEnum.READ_PURCHASE_DOWN_PAYMENTS,
                },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditPurchaseDownPayment />,
                handle: {
                  permission: PermissionEnum.UPDATE_PURCHASE_DOWN_PAYMENTS,
                },
              },
            ],
          },
          {
            path: "incoming-sales-invoices",
            children: [
              {
                index: true,
                element: <IncomingSalesInvoices />,
                handle: { permission: PermissionEnum.READ_SALES_INVOICES },
              },
              {
                path: ":salesInvoiceReferenceNumber/create-purchase-invoice",
                element: <CreatePurchaseInvoiceFromSalesInvoice />,
                handle: {
                  permission: PermissionEnum.CREATE_PURCHASE_INVOICES,
                },
              },
            ],
          },
          {
            path: "purchase-payments",
            children: [
              {
                index: true,
                element: <PurchasePayments />,
                handle: { permission: PermissionEnum.READ_PURCHASE_PAYMENTS },
              },
              {
                path: "create",
                element: <CreatePurchasePayment />,
                handle: { permission: PermissionEnum.CREATE_PURCHASE_PAYMENTS },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditPurchasePayment />,
                handle: { permission: PermissionEnum.READ_PURCHASE_PAYMENTS },
              },
              {
                path: ":referenceNumber",
                element: <PurchasePayment />,
                handle: { permission: PermissionEnum.READ_PURCHASE_PAYMENTS },
              },
            ],
          },
          {
            path: "purchase-returns",
            children: [
              {
                index: true,
                element: <PurchaseReturns />,
                handle: { permission: PermissionEnum.READ_PURCHASE_RETURNS },
              },
              {
                path: "create",
                element: <CreatePurchaseReturn />,
                handle: { permission: PermissionEnum.CREATE_PURCHASE_RETURNS },
              },
              {
                path: ":referenceNumber",
                element: <PurchaseReturn />,
                handle: { permission: PermissionEnum.READ_PURCHASE_RETURNS },
              },
            ],
          },
          {
            path: "suppliers",
            children: [
              {
                index: true,
                element: <Suppliers />,
                handle: { permission: PermissionEnum.READ_SUPPLIERS },
              },
              {
                path: "create",
                element: <CreateSupplier />,
                handle: { permission: PermissionEnum.CREATE_SUPPLIERS },
              },
              {
                path: ":supplierId/edit",
                element: <EditSupplier />,
                handle: { permission: PermissionEnum.UPDATE_SUPPLIERS },
              },
            ],
          },
          {
            path: "supplier-categories",
            children: [
              {
                index: true,
                element: <SupplierCategories />,
                handle: { permission: PermissionEnum.READ_SUPPLIER_CATEGORIES },
              },
            ],
          },
          {
            path: "payment-terms",
            children: [
              {
                index: true,
                element: <PaymentTerms />,
                handle: { permission: PermissionEnum.READ_PAYMENT_TERMS },
              },
            ],
          },
        ],
      },
      {
        path: "inventory",
        children: [
          {
            index: true,
            element: <Navigate to="products" replace />,
          },
          {
            path: "lens-products",
            children: [
              {
                index: true,
                element: <LensProducts />,
                handle: { permission: PermissionEnum.READ_PRODUCTS },
              },
              {
                path: "create",
                element: <CreateLensProduct />,
                handle: { permission: PermissionEnum.CREATE_PRODUCTS },
              },
              {
                path: ":productId/edit",
                element: <EditLensProduct />,
                handle: { permission: PermissionEnum.UPDATE_PRODUCTS },
              },
            ],
          },
          {
            path: "products",
            children: [
              {
                index: true,
                element: <Products />,
                handle: { permission: PermissionEnum.READ_PRODUCTS },
              },
              {
                path: "create",
                element: <CreateProduct />,
                handle: { permission: PermissionEnum.CREATE_PRODUCTS },
              },
              {
                path: ":productId/edit",
                element: <EditProduct />,
                handle: { permission: PermissionEnum.UPDATE_PRODUCTS },
              },
            ],
          },
          {
            path: "product-categories",
            element: <ProductCategories />,
            handle: { permission: PermissionEnum.READ_PRODUCT_CATEGORIES },
          },
          {
            path: "product-variant-attributes",
            element: <ProductVariantAttributes />,
            handle: {
              permission: PermissionEnum.READ_PRODUCT_VARIANT_ATTRIBUTES,
            },
          },
          {
            path: "unit-of-measurements",
            element: <UnitOfMeasurements />,
            handle: {
              permission: PermissionEnum.READ_UNIT_OF_MEASUREMENTS,
            },
          },
          {
            path: "warehouses",
            children: [
              {
                index: true,
                element: <Warehouses />,
                handle: { permission: PermissionEnum.READ_WAREHOUSES },
              },
              {
                path: "create",
                element: <CreateWarehouse />,
                handle: { permission: PermissionEnum.CREATE_WAREHOUSES },
              },
              {
                path: ":warehouseId/edit",
                element: <EditWarehouse />,
                handle: { permission: PermissionEnum.UPDATE_WAREHOUSES },
              },
            ],
          },
          {
            path: "warehouse-stocks",
            element: <WarehouseStocks />,
            handle: {
              permission: PermissionEnum.READ_WAREHOUSES,
            },
          },
          {
            path: "stock-adjustments",
            children: [
              {
                index: true,
                element: <StockAdjustments />,
                handle: {
                  permission: PermissionEnum.READ_STOCK_ADJUSTMENTS,
                },
              },
              {
                path: "create",
                element: <CreateStockAdjustment />,
                handle: {
                  permission: PermissionEnum.CREATE_STOCK_ADJUSTMENTS,
                },
              },
              {
                path: ":referenceNumber",
                element: <StockAdjustment />,
                handle: {
                  permission: PermissionEnum.READ_STOCK_ADJUSTMENTS,
                },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditStockAdjustment />,
                handle: {
                  permission: PermissionEnum.UPDATE_STOCK_ADJUSTMENTS,
                },
              },
            ],
          },
        ],
      },
      {
        path: "sales",
        children: [
          {
            index: true,
            element: <Navigate to="sales-orders" replace />,
          },
          {
            path: "sales-requests",
            children: [
              {
                index: true,
                element: <SalesRequests />,
                handle: { permission: PermissionEnum.READ_SALES_ORDERS },
              },
              {
                path: ":referenceNumber",
                element: <SalesRequest />,
                handle: { permission: PermissionEnum.READ_SALES_ORDERS },
              },
            ],
          },
          {
            path: "sales-orders",
            children: [
              {
                index: true,
                element: <SalesOrders />,
                handle: { permission: PermissionEnum.READ_SALES_ORDERS },
              },
              {
                path: "create",
                element: <CreateSalesOrder />,
                handle: { permission: PermissionEnum.CREATE_SALES_ORDERS },
              },
              {
                path: "create-manual",
                element: <CreateManualSalesOrder />,
                handle: { permission: PermissionEnum.CREATE_SALES_ORDERS },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditSalesOrder />,
                handle: { permission: PermissionEnum.UPDATE_SALES_ORDERS },
              },
              {
                path: ":referenceNumber",
                element: <SalesOrder />,
                handle: { permission: PermissionEnum.READ_SALES_ORDERS },
              },
            ],
          },
          {
            path: "sales-down-payments",
            children: [
              {
                index: true,
                element: <SalesDownPayments />,
                handle: { permission: PermissionEnum.READ_SALES_DOWN_PAYMENTS },
              },
              {
                path: "create",
                element: <CreateSalesDownPayment />,
                handle: {
                  permission: PermissionEnum.CREATE_SALES_DOWN_PAYMENTS,
                },
              },
              {
                path: ":referenceNumber",
                element: <SalesDownPayment />,
                handle: { permission: PermissionEnum.READ_SALES_DOWN_PAYMENTS },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditSalesDownPayment />,
                handle: {
                  permission: PermissionEnum.UPDATE_SALES_DOWN_PAYMENTS,
                },
              },
            ],
          },
          {
            path: "delivery-orders",
            children: [
              {
                index: true,
                element: <DeliveryOrders />,
                handle: { permission: PermissionEnum.READ_DELIVERY_ORDERS },
              },
              {
                path: "create",
                element: <CreateDeliveryOrder />,
                handle: { permission: PermissionEnum.CREATE_DELIVERY_ORDERS },
              },
              {
                path: ":referenceNumber",
                element: <DeliveryOrder />,
                handle: { permission: PermissionEnum.READ_DELIVERY_ORDERS },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditDeliveryOrder />,
                handle: { permission: PermissionEnum.READ_DELIVERY_ORDERS },
              },
            ],
          },
          {
            path: "sales-invoices",
            children: [
              {
                index: true,
                element: <SalesInvoices />,
                handle: { permission: PermissionEnum.READ_SALES_INVOICES },
              },
              {
                path: "create",
                element: <CreateSalesInvoice />,
                handle: { permission: PermissionEnum.CREATE_SALES_INVOICES },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditSalesInvoice />,
                handle: { permission: PermissionEnum.READ_SALES_INVOICES },
              },
              {
                path: ":referenceNumber",
                element: <SalesInvoice />,
                handle: { permission: PermissionEnum.READ_SALES_INVOICES },
              },
            ],
          },
          {
            path: "sales-payments",
            children: [
              {
                index: true,
                element: <SalesPayments />,
                handle: { permission: PermissionEnum.READ_SALES_PAYMENTS },
              },
              {
                path: "create",
                element: <CreateSalesPayment />,
                handle: { permission: PermissionEnum.CREATE_SALES_PAYMENTS },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditSalesPayment />,
                handle: { permission: PermissionEnum.READ_SALES_PAYMENTS },
              },
              {
                path: ":referenceNumber",
                element: <SalesPayment />,
                handle: { permission: PermissionEnum.READ_SALES_PAYMENTS },
              },
            ],
          },
          {
            path: "sales-returns",
            children: [
              {
                index: true,
                element: <SalesReturns />,
                handle: { permission: PermissionEnum.READ_SALES_RETURNS },
              },
              {
                path: "create",
                element: <CreateSalesReturn />,
                handle: { permission: PermissionEnum.CREATE_SALES_RETURNS },
              },
              {
                path: ":referenceNumber",
                element: <SalesReturn />,
                handle: { permission: PermissionEnum.READ_SALES_RETURNS },
              },
            ],
          },
          {
            path: "incoming-purchase-payments",
            children: [
              {
                index: true,
                element: <IncomingPurchasePayments />,
                handle: { permission: PermissionEnum.READ_PURCHASE_PAYMENTS },
              },
              {
                path: ":purchasePaymentReferenceNumber/create-sales-payment",
                element: <CreateSalesPaymentFromPurchasePayment />,
                handle: { permission: PermissionEnum.CREATE_SALES_PAYMENTS },
              },
            ],
          },
          {
            path: "incoming-purchase-returns",
            children: [
              {
                index: true,
                element: <IncomingPurchaseReturns />,
                handle: { permission: PermissionEnum.READ_PURCHASE_RETURNS },
              },
              {
                path: ":purchaseReturnReferenceNumber/create-sales-return",
                element: <CreateSalesReturnFromPurchaseReturn />,
                handle: { permission: PermissionEnum.CREATE_SALES_RETURNS },
              },
            ],
          },
          {
            path: "customer-claims",
            children: [
              {
                path: "receive",
                children: [
                  {
                    index: true,
                    element: <ReceiveCustomerClaims />,
                    handle: { permission: PermissionEnum.READ_CUSTOMER_CLAIMS },
                  },
                  {
                    path: "create",
                    element: <CreateReceiveCustomerClaim />,
                    handle: {
                      permission: PermissionEnum.CREATE_CUSTOMER_CLAIMS,
                    },
                  },
                  {
                    path: ":referenceNumber",
                    element: <CustomerClaim />,
                    handle: { permission: PermissionEnum.READ_CUSTOMER_CLAIMS },
                  },
                ],
              },
              {
                path: "delivery",
                children: [
                  {
                    index: true,
                    element: <DeliveryCustomerClaims />,
                    handle: { permission: PermissionEnum.READ_CUSTOMER_CLAIMS },
                  },
                  {
                    path: ":receiveClaimReferenceNumber/create",
                    element: <CreateDeliveryCustomerClaim />,
                    handle: {
                      permission: PermissionEnum.CREATE_CUSTOMER_CLAIMS,
                    },
                  },
                  {
                    path: ":referenceNumber",
                    element: <CustomerClaim />,
                    handle: { permission: PermissionEnum.READ_CUSTOMER_CLAIMS },
                  },
                ],
              },
            ],
          },
          {
            path: "sales-order-default-general-product-variants",
            element: <SalesOrderDefaultGeneralProductVariants />,
          },
          {
            path: "payment-methods",
            element: <PaymentMethods />,
            handle: { permission: PermissionEnum.READ_PAYMENT_METHODS },
          },
          {
            path: "customers",
            element: <Customers />,
            handle: { permission: PermissionEnum.READ_CUSTOMERS },
          },
        ],
      },
      {
        path: "general-ledger",
        children: [
          {
            path: "chart-of-accounts",
            element: <ChartOfAccounts />,
            handle: { permission: PermissionEnum.READ_CHART_OF_ACCOUNTS },
          },
          {
            path: "outlet-chart-of-accounts",
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <OutletChartOfAccounts />,
                handle: {
                  permission: PermissionEnum.READ_OUTLET_CHART_OF_ACCOUNTS,
                },
              },
              {
                path: "create",
                element: <CreateOutletChartOfAccount />,
                handle: {
                  permission: PermissionEnum.CREATE_OUTLET_CHART_OF_ACCOUNTS,
                },
              },
              {
                path: ":id/edit",
                element: <EditOutletChartOfAccount />,
                handle: {
                  permission: PermissionEnum.UPDATE_OUTLET_CHART_OF_ACCOUNTS,
                },
              },
              {
                path: ":id",
                element: <OutletChartOfAccount />,
                handle: {
                  permission: PermissionEnum.READ_OUTLET_CHART_OF_ACCOUNTS,
                },
              },
            ],
          },
          {
            path: "journal-vouchers",
            children: [
              {
                index: true,
                element: <JournalVouchers />,
                handle: { permission: PermissionEnum.READ_JOURNAL_VOUCHERS },
              },
              {
                path: "create",
                element: <CreateJournalVoucher />,
                handle: { permission: PermissionEnum.CREATE_JOURNAL_VOUCHERS },
              },
              {
                path: ":referenceNumber",
                element: <JournalVoucher />,
                handle: { permission: PermissionEnum.READ_JOURNAL_VOUCHERS },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditJournalVoucher />,
                handle: { permission: PermissionEnum.UPDATE_JOURNAL_VOUCHERS },
              },
            ],
          },
          {
            path: "other-payments",
            children: [
              {
                index: true,
                element: <OtherPayments />,
                handle: { permission: PermissionEnum.READ_OTHER_PAYMENTS },
              },
              {
                path: "create",
                element: <CreateOtherPayment />,
                handle: { permission: PermissionEnum.CREATE_OTHER_PAYMENTS },
              },
              {
                path: ":referenceNumber",
                element: <OtherPayment />,
                handle: { permission: PermissionEnum.READ_OTHER_PAYMENTS },
              },
              {
                path: ":referenceNumber/edit",
                element: <EditOtherPayment />,
                handle: { permission: PermissionEnum.UPDATE_OTHER_PAYMENTS },
              },
            ],
          },
          {
            path: "trial-balance",
            element: <TrialBalance />,
            handle: { permission: PermissionEnum.READ_CHART_OF_ACCOUNTS },
          },
        ],
      },
      {
        path: "report",
        children: [
          {
            path: "sales-orders",
            element: <SalesOrderReport />,
            handle: { permission: PermissionEnum.READ_SALES_ORDER_REPORT },
          },
          {
            path: "sales-order-payments",
            element: <SalesOrderPaymentReport />,
            handle: {
              permission: PermissionEnum.READ_SALES_ORDER_PAYMENT_REPORT,
            },
          },
          {
            path: "expenses",
            element: <ExpenseReport />,
            handle: { permission: PermissionEnum.READ_EXPENSE_REPORT },
          },
          {
            path: "sales-order-product-variants",
            children: [
              {
                index: true,
                element: <Navigate to="retail" replace />,
              },
              {
                path: "retail",
                element: <SalesOrderProductVariantRetailReport />,
                handle: {
                  permission: PermissionEnum.READ_SALES_ORDER_PRODUCT_REPORT,
                },
              },
              {
                path: "hub",
                element: <SalesOrderProductVariantHubReport />,
                handle: {
                  permission: PermissionEnum.READ_SALES_ORDER_PRODUCT_REPORT,
                },
              },
            ],
          },
          {
            path: "cost-of-goods-sold",
            element: <CostOfGoodsSoldReport />,
            handle: {
              permission: PermissionEnum.READ_COST_OF_GOODS_SOLD_REPORT,
            },
          },
          {
            path: "inventory-value",
            element: <InventoryValueReport />,
            handle: {
              permission: PermissionEnum.READ_INVENTORY_VALUE_REPORT,
            },
          },
        ],
      },
    ],
  },
  {
    path: "renew-password",
    element: (
      <RequireAuth>
        <RenewPassword />
      </RequireAuth>
    ),
  },
  {
    path: "*",
    Component: NotFound,
  },
]);

export default router;
